import { useCallback, useEffect, useState } from "react";
import { RegexValidatorResponse } from "../../models/regex/RegexValidator";
import {
  ValidationRule,
  ValidationRuleData,
} from "../../models/validation/ValidationRule";
import Icon from "../Icon";
import { InputGroup } from "../inputGroup/InputGroup";
import { Label } from "../inputGroup/Label";
import { RegexError } from "../regex/RegexError";
import { RegexInput } from "../regex/RegexInput";
import "../../styles/validation/validationRuleEditor.css";
import { Button } from "../buttons/Button";
import { AdvancedTooltip } from "../tooltips/AdvancedTooltip";
import { TTValidationDeleteRule } from "../tooltips/ConfigurationTooltips";
import { ClearableInput } from "../inputs/ClearableInput";

export {
  ValidationRuleComp as ValidationRuleEditor,
  ValidationStatus,
  ValidationStatusTT,
  ValidationStatuses,
};
type ValidationStatuses = "untested" | "loading" | "invalid" | "valid";
type KProps = {
  display: "edit" | "view";
  disabled?: boolean;
  testValidationStatus: ValidationStatuses;
  rule: ValidationRule;
  /**
   * Return a new ValidationRule object, with the oldRuleId of the object to replace.
   * @return '' if rule is valid, otherwise error message
   */
  update: (
    rule: ValidationRule | null,
    oldRuleId: string,
  ) => string | undefined;
};

const ValidationRuleComp = (props: KProps) => {
  const [oldRuleId, setOldRuleId] = useState<string>(props.rule.getRuleId());
  // set oldRuleId each time rule changes, set status to invalid if regex has error
  useEffect(() => setOldRuleId(props.rule.getRuleId()), [props.rule]);
  const rule = props.rule.toData();

  return (
    <>
      {props.display === "edit" && (
        <Editor //key={oldRuleId}
          disabled={props.disabled}
          testValidationStatus={props.testValidationStatus}
          rule={rule}
          delete={() => props.update(null, oldRuleId)}
          update={(label, message, regexError) => {
            rule.label = label;
            rule.errorMessage = message;
            // update regex and valid ness
            if (regexError) {
              // regex rule changed
              rule._valid = regexError.valid;
              rule.regex = regexError.regex;
            }
            return props.update(new ValidationRule(rule), oldRuleId);
          }}
        />
      )}
      {props.display === "view" && (
        <Display
          rule={rule}
          testValidationStatus={props.testValidationStatus}
        />
      )}
    </>
  );
};

type DisplayProps = {
  rule: ValidationRuleData;
  testValidationStatus: ValidationStatuses;
};

const Display = (props: DisplayProps) => {
  return (
    <div
      key={ValidationRule.getRuleId(props.rule)}
      className={`validationRuleEditor`}
    >
      <InputGroup button>
        <Label>Label</Label>
        <input type={"text"} value={props.rule.label} disabled />
      </InputGroup>
      <InputGroup button>
        <Label>Error Description</Label>
        <input type={"text"} value={props.rule.errorMessage} disabled />
      </InputGroup>
      {/* edit Regex rule */}
      <InputGroup button>
        <Label>Regex</Label>
        <input
          type={"text"}
          className={"monospace"}
          value={props.rule.regex}
          disabled
        />
        <span>
          <ValidationStatus status={props.testValidationStatus} />
        </span>
      </InputGroup>
    </div>
  );
};

type EditorProps = {
  disabled?: boolean;
  testValidationStatus: ValidationStatuses;
  rule: ValidationRuleData;
  /** Update label, error message, and validated regex
   * @return '' if rule is valid, otherwise error message
   */
  update: (
    label: string,
    message: string,
    regexError: RegexValidatorResponse | undefined,
  ) => string | undefined;
  /** Delete rule */
  delete: () => void;
};
/**
 * Component acts as a glorified regex input componennt,
 */
const Editor = (props: EditorProps) => {
  /* Assume regex is valid by default, prevent call to API on mount  */
  const [regexError, setRegexError] = useState<
    RegexValidatorResponse | undefined
  >({
    reasons: [],
    regex: props.rule.regex,
    valid: true,
  });
  /**
   * Set when rule cannot be updated. Expected errors are
   * - regex is empty
   * - rule is duplicated
   */
  const [ruleError, setRuleError] = useState<string>();
  const rule = props.rule;
  const [label, setLabel] = useState(rule.label);
  const [message, setMessage] = useState(rule.errorMessage);
  const [regex, setRegex] = useState(rule.regex);

  // called when inputs blurred. Checks regex is validated and dispalys error if rule invalid
  const callbackUpdateOnBlur = useCallback(() => {
    // if regex is invalid/unverified
    if (regexError?.valid == undefined || regexError.valid == false) {
      return;
    }
    // if values have not changed
    if (
      props.rule.regex === regex &&
      props.rule.label === label &&
      props.rule.errorMessage === message
    ) {
      return;
    }
    // if rule not valid, display an error here
    const ruleUpdateErr = props.update(label, message, regexError);
    if (ruleUpdateErr) {
      // display an exception about the rule
      return setRuleError(ruleUpdateErr);
    }
    // clear past errors
    setRuleError(undefined);
  }, [regexError, label, message, regex]);

  // display error when RegexError changes
  useEffect(() => {
    callbackUpdateOnBlur();
  }, [regexError]);

  // calculate className as one of 'green' | 'red' | 'grey'
  const className = "grey";

  const hasRuleError = ruleError != undefined && ruleError !== "";

  return (
    <div className={`validationRuleEditor ${className}`}>
      <InputGroup button>
        <Label>Label</Label>
        <ClearableInput
          value={label}
          disabled={props.disabled}
          placeholder={"Rule Label (example: Invoice Number INV- Prefix)"}
          showClearButton={() => !props.disabled}
          onBlur={() => callbackUpdateOnBlur()}
          onChange={(e) => {
            setLabel(e.target.value);
          }}
          onClear={() => {
            setLabel("");
          }}
        />
        {!props.disabled && (
          <AdvancedTooltip short tooltipText={TTValidationDeleteRule}>
            {(tProps) => (
              <Button
                {...tProps}
                onClick={() => {
                  // delete
                  props.delete();
                }}
              >
                Delete
                <Icon altText={""} icon={"trash"} />
              </Button>
            )}
          </AdvancedTooltip>
        )}
      </InputGroup>

      {/* edit Regex rule */}
      <InputGroup button>
        <Label>Regex</Label>
        <RegexInput
          initialValue={regex}
          disabled={props.disabled}
          placeholder={"Regex (example: ^INV-.+)"}
          updated={(result) => {
            setRegex(result.regex);
            setRegexError(result);
            callbackUpdateOnBlur();
          }}
        />
        <ValidationStatus
          status={hasRuleError ? "untested" : props.testValidationStatus}
        />
      </InputGroup>

      <InputGroup>
        <Label>Error Description</Label>
        <textarea
          value={message}
          disabled={props.disabled}
          placeholder={
            "Error Description (example: Invoice Number must start with INV-)"
          }
          onBlur={() => callbackUpdateOnBlur()}
          onChange={(e) => {
            setMessage(e.target.value);
          }}
          style={{
            // Resize vertically
            resize: "block",
            width: "100%",
          }}
        />
      </InputGroup>

      <div className={"errorContainer"}>
        <RegexError error={regexError} />
      </div>

      <footer>
        {hasRuleError && <span className={"error"}>{ruleError}</span>}
      </footer>
    </div>
  );
};

function ValidationStatusTooltip(status: ValidationStatuses): string {
  if (status === "valid") {
    return "The validation rules for this field passed for tested value.";
  } else if (status === "invalid") {
    return "The validation rules for this field field against the tested value. This field would stop for manual validation.";
  }
  return "";
}
type ValidationStatusProps = {
  status: ValidationStatuses;
};
/* Show */
const ValidationStatusTT = (props: ValidationStatusProps) => {
  return (
    <AdvancedTooltip
      short
      tooltipText={() => ValidationStatusTooltip(props.status)}
      possiblePlacements={["right-center"]}
    >
      {(tProps) => <ValidationStatus tProps={tProps} {...props} />}
    </AdvancedTooltip>
  );
};
const ValidationStatus = (props: ValidationStatusProps & { tProps?: any }) => {
  return (
    <span {...props.tProps} className={"ValidationStatus"}>
      {/* if rule is not configured, don't show anything */}
      {props.status === "invalid" && (
        <Icon altText={``} color={"red"} icon={"times-circle"} />
      )}
      {props.status === "valid" && (
        <Icon altText={``} color={"green"} icon={"check-circle"} />
      )}
    </span>
  );
};
